import React, { useCallback, useRef } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import RadioGroup from '@grnhse/seedling/lib/birch/components/radio_group';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { withLocalePrefix } from 'shared/utils/translation';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  MultiDropdown,
  useMultiDropdown,
  SingleDropdown,
  useSingleDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { useMyGreenhouseTabContext } from 'talent_matching/contexts/my_greenhouse_tab_context';
import { standardRequest } from 'shared/utils/request';
import {
  REMOTE_CANDIDATES_ANY,
  REMOTE_CANDIDATES_OPTIONS,
  type RemoteCandidatesValue,
} from './remote_candidates';
const t = withLocalePrefix('talent_matching.filter_row');

// No employment-type filter: AdeptID has no native field for it and the feed has no
// custom-filter slot carrying it, so it could only ever narrow nothing. Adding one
// means a mapper change plus a re-push of every candidate.
const WORKSITE_OPTIONS: Item[] = [
  { label: 'Remote', value: 'remote' },
  { label: 'Hybrid', value: 'hybrid' },
  { label: 'On-site', value: 'on_site' },
];

const tLang = withLocalePrefix('job_seekers.language_names');

// Matches the list of languages allowed by job-seekers codebase
// https://github.com/grnhse/job-seekers/blob/main/app/data/languages.yml
const LANGUAGE_OPTIONS: Item[] = [
  { label: tLang('arabic'), value: 'arabic' },
  { label: tLang('bengali'), value: 'bengali' },
  { label: tLang('cantonese'), value: 'cantonese' },
  { label: tLang('dutch'), value: 'dutch' },
  { label: tLang('english'), value: 'english' },
  { label: tLang('farsi'), value: 'farsi' },
  { label: tLang('finnish'), value: 'finnish' },
  { label: tLang('french'), value: 'french' },
  { label: tLang('german'), value: 'german' },
  { label: tLang('hebrew'), value: 'hebrew' },
  { label: tLang('hindi'), value: 'hindi' },
  { label: tLang('indonesian'), value: 'indonesian' },
  { label: tLang('italian'), value: 'italian' },
  { label: tLang('japanese'), value: 'japanese' },
  { label: tLang('korean'), value: 'korean' },
  { label: tLang('mandarin_chinese'), value: 'mandarin_chinese' },
  { label: tLang('marathi'), value: 'marathi' },
  { label: tLang('norwegian'), value: 'norwegian' },
  { label: tLang('polish'), value: 'polish' },
  { label: tLang('portuguese'), value: 'portuguese' },
  { label: tLang('russian'), value: 'russian' },
  { label: tLang('spanish'), value: 'spanish' },
  { label: tLang('swedish'), value: 'swedish' },
  { label: tLang('tagalog'), value: 'tagalog' },
  { label: tLang('tajik'), value: 'tajik' },
  { label: tLang('tamil'), value: 'tamil' },
  { label: tLang('telugu'), value: 'telugu' },
  { label: tLang('thai'), value: 'thai' },
  { label: tLang('turkish'), value: 'turkish' },
  { label: tLang('urdu'), value: 'urdu' },
  { label: tLang('vietnamese'), value: 'vietnamese' },
  { label: tLang('other'), value: 'other' },
];

type MultiSelectFieldProps = {
  label: string;
  ariaLabel: string;
  options: Item[];
  selectedItems: Item[];
  onSelectedItemsChange: (items: Item[]) => void;
};

const MultiSelectField = ({
  label,
  ariaLabel,
  options,
  selectedItems,
  onSelectedItemsChange,
}: MultiSelectFieldProps) => {
  const dropdownProps = useMultiDropdown({
    fetchItems: async () => ({ items: options }),
    selectedItems,
    onSelectedItemsChange: (changes) =>
      onSelectedItemsChange(changes.selectedItems ?? []),
  });

  return (
    <MultiDropdown dropdownProps={dropdownProps}>
      <MultiDropdown.Field
        label={label}
        placeholder={t('advanced_filters.select')}
        ariaLabel={ariaLabel}
        condensedUnitName={t('work_preferences.selections')}
      />
      <MultiDropdown.Flyout disablePortal />
    </MultiDropdown>
  );
};

const WorkPreferencesFiltersGroup = () => {
  const { paths } = useMyGreenhouseTabContext();
  const { watch, setValue } = useFormContext();
  const [worksites, locations, languages, remoteCandidates, willingToRelocate] =
    watch([
      'workPreferenceWorksites',
      'workPreferenceLocations',
      'workPreferenceLanguages',
      'remoteCandidates',
      'workPreferenceWillingToRelocate',
    ]);

  const selectedWorksites = (worksites as Item[]) || [];
  const selectedLocations = (locations as Item[]) || [];
  const selectedLocation = selectedLocations[0] ?? null;
  const selectedLanguages = (languages as Item[]) || [];
  const selectedRemoteCandidates =
    (remoteCandidates as RemoteCandidatesValue | null) || null;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingResolveRef = useRef<((value: { items: Item[] }) => void) | null>(
    null
  );

  const fetchLocationItems = useCallback(
    (term: string) => {
      if (!term || term.trim().length < 2) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        if (pendingResolveRef.current) {
          pendingResolveRef.current({ items: [] });
          pendingResolveRef.current = null;
        }
        return Promise.resolve({ items: [] });
      }

      if (pendingResolveRef.current) {
        pendingResolveRef.current({ items: [] });
        pendingResolveRef.current = null;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      return new Promise<{ items: Item[] }>((resolve) => {
        pendingResolveRef.current = resolve;

        debounceRef.current = setTimeout(async () => {
          pendingResolveRef.current = null;
          try {
            const response = await standardRequest.get(
              paths.locationAutocompletePath,
              { params: { term } }
            );

            const features = response.data?.results || [];
            resolve({
              items: features.map(
                (f: {
                  properties?: { label?: string; layer?: string };
                  geometry?: { coordinates?: [number, number] };
                }) => {
                  const [lon, lat] = f.geometry?.coordinates || [0, 0];
                  const layer = f.properties?.layer || 'locality';
                  return {
                    label: f.properties?.label || '',
                    value: `${lat},${lon},${layer}`,
                  };
                }
              ),
            });
          } catch {
            resolve({ items: [] });
          }
        }, 400);
      });
    },
    [paths.locationAutocompletePath]
  );

  const locationDropdownProps = useSingleDropdown({
    fetchItems: fetchLocationItems,
    selectedItem: selectedLocation,
    onSelectedItemChange: (changes) =>
      setValue(
        'workPreferenceLocations',
        changes.selectedItem ? [changes.selectedItem] : [],
        { shouldDirty: true }
      ),
    preventLoadOnMount: true,
  });

  const requiresLocation =
    selectedRemoteCandidates === 'local_only' && !selectedLocation;

  const remoteCandidatesOptions = REMOTE_CANDIDATES_OPTIONS.map((option) =>
    option.value === 'local_only' && requiresLocation
      ? {
          ...option,
          helperText: t('work_preferences.local_only_requires_location'),
        }
      : option
  );

  const appliedCount = [
    selectedLanguages.length > 0,
    selectedWorksites.length > 0,
    selectedLocations.length > 0,
    !!selectedRemoteCandidates,
    willingToRelocate,
  ].filter(Boolean).length;

  const buttonLabel = `${t('work_preferences.title')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  const clear = () => {
    setValue('workPreferenceLanguages', [], { shouldDirty: true });
    setValue('workPreferenceWorksites', [], { shouldDirty: true });
    setValue('workPreferenceLocations', [], { shouldDirty: true });
    setValue('remoteCandidates', null, { shouldDirty: true });
    setValue('workPreferenceWillingToRelocate', false, { shouldDirty: true });
  };

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('work_preferences.filters_title')}
      width="500px"
    >
      <Stack spacing={3}>
        <MultiSelectField
          label={t('work_preferences.languages_spoken')}
          ariaLabel={t('work_preferences.languages_spoken')}
          options={LANGUAGE_OPTIONS}
          selectedItems={selectedLanguages}
          onSelectedItemsChange={(items) =>
            setValue('workPreferenceLanguages', items, { shouldDirty: true })
          }
        />

        <MultiSelectField
          label={t('work_preferences.worksites')}
          ariaLabel={t('work_preferences.worksites')}
          options={WORKSITE_OPTIONS}
          selectedItems={selectedWorksites}
          onSelectedItemsChange={(items) =>
            setValue('workPreferenceWorksites', items, { shouldDirty: true })
          }
        />

        <SingleDropdown dropdownProps={locationDropdownProps}>
          <SingleDropdown.Field
            label={t('location.title')}
            placeholder={t('advanced_filters.select')}
            ariaLabel={t('location.title')}
            clearable
            required={requiresLocation}
          />
          <SingleDropdown.Flyout disablePortal />
        </SingleDropdown>

        <RadioGroup
          name="remoteCandidates"
          legend={t('work_preferences.location_match')}
          options={remoteCandidatesOptions}
          value={selectedRemoteCandidates ?? REMOTE_CANDIDATES_ANY}
          onChange={(value) =>
            setValue(
              'remoteCandidates',
              value === REMOTE_CANDIDATES_ANY
                ? null
                : (value as RemoteCandidatesValue),
              { shouldDirty: true }
            )
          }
        />

        <Checkbox
          label={t('work_preferences.willing_to_relocate')}
          value="willingToRelocate"
          checked={!!willingToRelocate}
          onChange={() =>
            setValue('workPreferenceWillingToRelocate', !willingToRelocate, {
              shouldDirty: true,
            })
          }
        />

        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('advanced_filters.clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default WorkPreferencesFiltersGroup;
